// Presentation map for the capability editor + summary — the ONE authored artifact
// the catalogue (ADR-025) doesn't carry: section/family grouping, icons, scale-vs-
// adjunct designation, and which keys carry help. The catalogue supplies the
// relationships (includes / requires / entitlement); this supplies how they show.
//
// Shared by every surface that grants or shows capability keys — a project role
// (create wizard · edit · detail · peek), an application credential's two lists
// (acts as itself · acts for a user), a personal access token — each passing the
// catalogue subset it may grant through `filterSections`: a wired family with
// nothing offered drops out; a coming-soon family always stays, so every surface
// advertises the platform's full capability picture (architect-ruled).
//
// Locked IA — Machine contracts lead (the machine-tier section; an application's
// self list is the only surface that offers it), then the four master sections in
// a uniform `section → family` shape: Operations · People & Access · Reports ·
// Administration. A family is either WIRED (real catalogued keys → controls) or
// `comingSoon` (a draft module not yet in the catalogue → a "coming soon" chip;
// its real keys slot in at the module's own design session).
//
// All `label` / `help` fields hold i18n KEYS in the `permissions` namespace
// (frontend/src/i18n/locales/*/permissions.json); the editor resolves them with
// t(). Help says what a key really lets the holder do and where it stops — never a
// restatement of the label.

import { ENTITLEMENT_FLAGS, type EntitlementFlag, type PermissionKey } from '@ems-app/schemas/permissions';
import type { icons } from '../../ui/icons.js';

export interface ScaleThing {
  kind: 'scale';
  id: string;
  /** i18n key. */
  label: string;
  /** Tier-2 help — i18n key; explains the resource where the label can't. */
  help?: string;
  /** Ordered access rungs (the SegmentedControl); a leading zero tier is added
   *  by the editor — "None" unless `noneLabel` renames it. */
  rungs: { key: PermissionKey; label: string }[];
  /** i18n key for the zero tier when "None" would lie — a family whose READ is
   *  inherited from the anchor (files) says "Read only": holding no key still
   *  reads what the anchor shows. */
  noneLabel?: string;
  /** Independent checkboxes alongside the scale (export, import, sub-resource reads). */
  adjuncts?: { key: PermissionKey; label: string; help?: string }[];
}

export interface ToggleThing {
  kind: 'toggle';
  id: string;
  label: string;
  help?: string;
  key: PermissionKey;
  /** A key whose reach is unusually wide (a bulk-plaintext door, a token export) —
   *  wears a "High-impact" chip so the grantor pauses. */
  highStakes?: boolean;
}

export type Thing = ScaleThing | ToggleThing;

export interface Family {
  id: string;
  /** i18n key. */
  label: string;
  icon: keyof typeof icons;
  /** Tier-1 help — i18n key; a one-line family intro shown on expand. */
  help?: string;
  /** Whole-family module gate — off ⇒ a "<module> off" chip, no controls. */
  entitlement?: EntitlementFlag;
  /** Draft module not yet catalogued ⇒ a "coming soon" chip (no controls). */
  comingSoon?: boolean;
  things?: Thing[];
}

export interface Section {
  id: string;
  /** i18n key. */
  label: string;
  families: Family[];
}

/** Every optional module ON — what a credential surface passes: an application key
 *  or a personal token is not bound to one project's module switches, so nothing is
 *  hidden there and the full capability surface stays visible (architect-ruled). */
export const ALL_ENTITLEMENTS_ON: ReadonlySet<EntitlementFlag> = new Set(ENTITLEMENT_FLAGS);

const VIEW = 'editor.view';
const MANAGE = 'editor.manage';

export const SECTIONS: Section[] = [
  {
    // Machine-tier contracts (ems-domain PERMISSIONS §Machine contracts): doors no
    // person opens; valid only in an application credential's self list, so this
    // section only ever survives `filterSections` for that one surface.
    id: 'machine',
    label: 'section.machine',
    families: [
      {
        id: 'machine',
        label: 'family.machine.label',
        icon: 'apiClient',
        help: 'family.machine.help',
        things: [
          { kind: 'toggle', id: 'external-identity', label: 'cap.externalIdentity.label', help: 'cap.externalIdentity.help', key: 'external_identity:resolve' },
          { kind: 'toggle', id: 'bot-fleet', label: 'cap.botFleet.label', help: 'cap.botFleet.help', key: 'bot_fleet:read', highStakes: true },
          { kind: 'toggle', id: 'observation-ingest', label: 'cap.observationIngest.label', help: 'cap.observationIngest.help', key: 'solution_config_observation:ingest' },
          { kind: 'toggle', id: 'cmdb-projection', label: 'cap.cmdbProjection.label', help: 'cap.cmdbProjection.help', key: 'cmdb_projection:read' },
          // Carries the device-target kinds narrowing list (the consumer's slot).
          { kind: 'toggle', id: 'device-targets', label: 'cap.deviceTargets.label', help: 'cap.deviceTargets.help', key: 'device_targets:read', highStakes: true },
        ],
      },
    ],
  },
  {
    id: 'operations',
    label: 'section.operations',
    families: [
      // Monitoring — the area key is live (it opens the Monitoring area); alarms, export and
      // device control join the family at the monitoring design session.
      {
        id: 'monitoring',
        label: 'family.monitoring.label',
        icon: 'monitoring',
        help: 'family.monitoring.help',
        things: [{ kind: 'toggle', id: 'monitoring-read', label: 'cap.monitoringRead.label', help: 'cap.monitoringRead.help', key: 'monitoring:read' }],
      },
      {
        id: 'sites',
        label: 'family.sites.label',
        icon: 'site',
        help: 'family.sites.help',
        things: [
          {
            kind: 'scale',
            id: 'site',
            label: 'cap.site',
            rungs: [
              { key: 'site:read', label: VIEW },
              { key: 'site:write', label: MANAGE },
            ],
            adjuncts: [
              { key: 'site:lifecycle', label: 'cap.siteLifecycle.label', help: 'cap.siteLifecycle.help' },
              { key: 'site:export', label: 'cap.siteExport' },
              { key: 'site:import', label: 'cap.siteImport' },
              { key: 'site.solution_config:write', label: 'cap.siteSolutionConfig.label', help: 'cap.siteSolutionConfig.help' },
              { key: 'site.contractual_config:write', label: 'cap.siteContractualConfig.label', help: 'cap.siteContractualConfig.help' },
              { key: 'site.legal:read', label: 'cap.siteLegal.label', help: 'cap.siteLegal.help' },
            ],
          },
        ],
      },
      {
        // The Files family (ems-domain PERMISSIONS §Files): read is the anchor's
        // (a site's or an asset's reader reads its files); the three verbs are an
        // ordered pair (upload < manage) and the review adjunct — evidence, never a
        // register write.
        id: 'files',
        label: 'family.files.label',
        icon: 'file',
        help: 'family.files.help',
        things: [
          {
            kind: 'scale',
            id: 'files',
            label: 'cap.files',
            // Read is the ANCHOR's (no files:read) — the zero tier still reads,
            // so "None" would lie.
            noneLabel: 'editor.readOnly',
            rungs: [
              { key: 'files:upload', label: 'cap.filesUpload' },
              { key: 'files:manage', label: 'cap.filesManage' },
            ],
            adjuncts: [{ key: 'files:review', label: 'cap.filesReview.label', help: 'cap.filesReview.help' }],
          },
        ],
      },
      {
        id: 'assets',
        label: 'family.assets.label',
        icon: 'asset',
        help: 'family.assets.help',
        things: [
          {
            kind: 'scale',
            id: 'asset',
            label: 'cap.asset',
            rungs: [
              { key: 'asset:read', label: VIEW },
              { key: 'asset:write', label: MANAGE },
            ],
            adjuncts: [
              { key: 'asset:export', label: 'cap.assetExport' },
              { key: 'asset:import', label: 'cap.assetImport' },
              { key: 'asset.private_fields:read', label: 'cap.assetPrivateFields.label', help: 'cap.assetPrivateFields.help' },
              { key: 'asset.models:manage', label: 'cap.assetModels.label', help: 'cap.assetModels.help' },
            ],
          },
          {
            kind: 'scale',
            id: 'asset-books',
            label: 'cap.assetBooks.label',
            help: 'cap.assetBooks.help',
            rungs: [
              { key: 'asset.books:read', label: VIEW },
              { key: 'asset.books:write', label: MANAGE },
            ],
          },
        ],
      },
      {
        id: 'invoicing',
        label: 'family.invoicing.label',
        icon: 'invoicing',
        entitlement: 'has_invoicing',
        help: 'family.invoicing.help',
        things: [
          {
            kind: 'scale',
            id: 'invoicing',
            label: 'cap.invoicing',
            rungs: [
              { key: 'invoicing:read', label: VIEW },
              { key: 'invoicing:validate', label: 'cap.invoicingValidate' },
            ],
            adjuncts: [
              { key: 'invoicing:validate_exception', label: 'cap.invoicingException.label', help: 'cap.invoicingException.help' },
            ],
          },
        ],
      },
      {
        // Warehouses & consumables (ems-domain PERMISSIONS §Warehouses & consumables):
        // the depot scale (read < write), then the stock verbs and the pooled
        // consumable-type vocabulary — enforced since the stock imports, so a
        // grant is meaningful ahead of the depot surfaces.
        id: 'warehouse',
        label: 'family.warehouse.label',
        icon: 'warehouse',
        help: 'family.warehouse.help',
        things: [
          {
            kind: 'scale',
            id: 'warehouse',
            label: 'cap.warehouse',
            rungs: [
              { key: 'warehouse:read', label: VIEW },
              { key: 'warehouse:write', label: MANAGE },
            ],
            adjuncts: [
              { key: 'warehouse.stock:write', label: 'cap.stockWrite.label', help: 'cap.stockWrite.help' },
              { key: 'warehouse.stock:approve', label: 'cap.stockApprove.label', help: 'cap.stockApprove.help' },
              { key: 'consumable.types:manage', label: 'cap.consumableTypes.label', help: 'cap.consumableTypes.help' },
            ],
          },
        ],
      },
      {
        // Vehicles & pools (ems-domain PERMISSIONS §Vehicles & pools): the fleet
        // record scale (read < write), then the holder and pool verbs. A driver
        // sees their own vehicle with no key; what a van holds rides the units'
        // own right — this family is the fleet itself.
        id: 'vehicles',
        label: 'family.vehicles.label',
        icon: 'vehicle',
        entitlement: 'has_vehicles',
        help: 'family.vehicles.help',
        things: [
          {
            kind: 'scale',
            id: 'vehicle',
            label: 'cap.vehicle',
            rungs: [
              { key: 'vehicle:read', label: VIEW },
              { key: 'vehicle:write', label: MANAGE },
            ],
            adjuncts: [
              { key: 'vehicle.driver:write', label: 'cap.vehicleDriver.label', help: 'cap.vehicleDriver.help' },
              { key: 'vehicle.pool:write', label: 'cap.vehiclePool.label', help: 'cap.vehiclePool.help' },
            ],
          },
        ],
      },
      {
        // The capture family — read + manage (ADR-073 §7: no create key; the
        // field force captures, the ticket is minted). Requests and work orders
        // add their own scales at their phases.
        id: 'tickets',
        label: 'family.tickets.label',
        icon: 'ticket',
        entitlement: 'has_field_force',
        help: 'family.tickets.help',
        things: [
          {
            kind: 'scale',
            id: 'ticket-capture',
            label: 'cap.ticketCapture.label',
            help: 'cap.ticketCapture.help',
            rungs: [
              { key: 'ticket.capture:read', label: VIEW },
              { key: 'ticket.capture:manage', label: MANAGE },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'people',
    label: 'section.people',
    families: [
      {
        id: 'users',
        label: 'family.users.label',
        icon: 'users',
        help: 'family.users.help',
        things: [
          // The ONE people key (ADR-050): accounts and roles, the scope of one's people, the
          // chains at the places one covers. Carries TWO narrowing lists (the roles
          // consumer's slot): the managed roles (whom) and the responsibility level-set
          // (which levels one may name chains at).
          { kind: 'toggle', id: 'users-manage', label: 'cap.usersManage.label', help: 'cap.usersManage.help', key: 'users:manage' },
          // Standalone from users:manage (the HR facet — people documents are not user administration).
          { kind: 'toggle', id: 'user-documents', label: 'cap.userDocuments.label', help: 'cap.userDocuments.help', key: 'user.documents:read' },
        ],
      },
      {
        // Teams (ADR-083 §5) — a key for the thing, team scope for which teams: `read` opens the
        // Teams page over the holder's team scope, `manage` runs them. A member sees their own
        // teams with no key. The family rides the field-force module.
        id: 'teams',
        label: 'family.teams.label',
        icon: 'team',
        entitlement: 'has_field_force',
        help: 'family.teams.help',
        things: [
          {
            kind: 'scale',
            id: 'team',
            label: 'cap.team.label',
            help: 'cap.team.help',
            rungs: [
              { key: 'team:read', label: VIEW },
              { key: 'team:manage', label: MANAGE },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'reports',
    label: 'section.reports',
    families: [
      { id: 'rep-invoicing', label: 'family.repInvoicing.label', icon: 'contract', comingSoon: true, entitlement: 'has_invoicing', help: 'family.repInvoicing.help' },
      // The first live report — one standalone toggle per report (seeing a report is exporting it); the
      // family's other reports join at the reports design session.
      {
        id: 'rep-monitoring',
        label: 'family.repMonitoring.label',
        icon: 'monitoring',
        help: 'family.repMonitoring.help',
        things: [{ kind: 'toggle', id: 'report-alarm-history', label: 'cap.reportAlarmHistory.label', help: 'cap.reportAlarmHistory.help', key: 'report:alarm_history' }],
      },
      { id: 'rep-energy', label: 'family.repEnergy.label', icon: 'energy', comingSoon: true, help: 'family.repEnergy.help' },
      { id: 'rep-diesel', label: 'family.repDiesel.label', icon: 'gauge', comingSoon: true, help: 'family.repDiesel.help' },
      { id: 'rep-field', label: 'family.repField.label', icon: 'maintenance', comingSoon: true, entitlement: 'has_field_force', help: 'family.repField.help' },
      { id: 'rep-compliance', label: 'family.repCompliance.label', icon: 'flag', comingSoon: true, help: 'family.repCompliance.help' },
      { id: 'rep-customer', label: 'family.repCustomer.label', icon: 'building', comingSoon: true, help: 'family.repCustomer.help' },
    ],
  },
  {
    id: 'administration',
    label: 'section.administration',
    families: [
      {
        id: 'reference',
        label: 'family.reference.label',
        icon: 'tag',
        help: 'family.reference.help',
        things: [{ kind: 'toggle', id: 'refdata', label: 'cap.refdata', key: 'reference_data:manage' }],
      },
      {
        id: 'contractual',
        label: 'family.contractual.label',
        icon: 'contract',
        entitlement: 'has_invoicing',
        help: 'family.contractual.help',
        things: [{ kind: 'toggle', id: 'contractual-manage', label: 'cap.contractualManage', key: 'contractual_config:manage' }],
      },
      {
        id: 'audit',
        label: 'family.audit.label',
        icon: 'history',
        help: 'family.audit.help',
        // Admin-implicit for project_admin; this toggle is the DELEGATION path
        // (the trusted-auditor case the catalogue names).
        things: [{ kind: 'toggle', id: 'audit-read', label: 'cap.auditRead', key: 'audit:read' }],
      },
      {
        id: 'api',
        label: 'family.api.label',
        icon: 'code',
        help: 'family.api.help',
        things: [{ kind: 'toggle', id: 'api-docs', label: 'cap.apiDocs', key: 'api_docs:read' }],
      },
    ],
  },
];

/** Every key a thing presents (rungs + adjuncts, or the toggle's key). */
export function keysOfThing(th: Thing): PermissionKey[] {
  return th.kind === 'scale' ? [...th.rungs.map((r) => r.key), ...(th.adjuncts ?? []).map((a) => a.key)] : [th.key];
}

/** The map narrowed to what ONE surface may grant. A rung / adjunct / toggle not in
 *  `offered` disappears; a wired family left with nothing drops; a coming-soon
 *  family always stays (the full platform surface is advertised everywhere); a
 *  section with no families drops. Pure — memoize at the call site. */
export function filterSections(sections: readonly Section[], offered: ReadonlySet<string>): Section[] {
  const out: Section[] = [];
  for (const sec of sections) {
    const families: Family[] = [];
    for (const fam of sec.families) {
      if (fam.comingSoon) {
        families.push(fam);
        continue;
      }
      const things: Thing[] = [];
      for (const th of fam.things ?? []) {
        if (th.kind === 'scale') {
          const rungs = th.rungs.filter((r) => offered.has(r.key));
          const adjuncts = (th.adjuncts ?? []).filter((a) => offered.has(a.key));
          if (rungs.length === 0 && adjuncts.length === 0) continue;
          things.push({ ...th, rungs, adjuncts });
        } else if (offered.has(th.key)) {
          things.push(th);
        }
      }
      if (things.length > 0) families.push({ ...fam, things });
    }
    if (families.length > 0) out.push({ ...sec, families });
  }
  return out;
}
